/**
 * Form.List row of an ordered-relation editor: the related id under
 * `valueField`, plus the server row `id` when the record already had one.
 * `position` is derived on submit, never hand-entered. A consumer that
 * carries more per row intersects its own fields on — see `SectionRow`.
 */
export type OrderedRow<Field extends string> = {
  id?: number | null;
  position?: number | null;
} & { [K in Field]?: number | null };

/** Upsert payload the endpoints take: the same row with everything the
 * server needs decided. Structurally `SectionIn`, a ranking upsert, … */
export type NormalizedRow<Field extends string> = { id?: number } & {
  [K in Field]: number;
} & { position: number };

type LooseRow = Record<string, unknown>;

/**
 * Rewrites positions 1..n from final row order for upsert. Row `id`s
 * survive so the server updates rows instead of recreating them; rows with
 * nothing picked are dropped.
 *
 * `extraFields` names the further row keys to carry through. Everything not
 * named is dropped: the edit forms seed their rows from whole server records,
 * so a blanket passthrough would echo nested objects and timestamps back at
 * the endpoint. An extra left unset stays absent, so the server's own default
 * applies rather than an explicit null.
 *
 * Callers that pass extras should wrap this in a typed helper rather than
 * calling it raw — `normalizeSectionRows` is the model.
 */
export const normalizeOrderedRows = <Field extends string>(
  rows: ReadonlyArray<OrderedRow<Field>> | undefined | null,
  valueField: Field,
  extraFields: readonly string[] = []
): Array<NormalizedRow<Field>> =>
  (rows ?? [])
    .map((row) => (row ?? {}) as LooseRow)
    .filter((row) => row[valueField] != null)
    .map((row, index) => ({
      ...(row.id != null ? { id: row.id } : {}),
      [valueField]: row[valueField],
      ...Object.fromEntries(
        extraFields
          .filter((key) => row[key] != null)
          .map((key) => [key, row[key]])
      ),
      position: index + 1,
    })) as Array<NormalizedRow<Field>>;

/** Picked ids in row order — the lookup input for card rows. */
export const orderedRowIds = <Field extends string>(
  rows: ReadonlyArray<OrderedRow<Field>> | undefined | null,
  valueField: Field
): number[] =>
  (rows ?? [])
    .map((row) => ((row ?? {}) as LooseRow)[valueField])
    .filter((id): id is number => id != null);
